/**
 * Studio Roulette's memory (AUTO-085): the last rolls, and which roll the Studio tab is currently
 * set up from. Browser storage only, and every read and write is guarded (private windows and cleared
 * site data make storage throw), so the tab still works without it.
 *
 * Rolls are stored whole, not just by seed: a roll with locked parts cannot be rebuilt from its seed.
 */
import type { RouletteRoll } from '@/shared/studioRoulette';

const HISTORY_KEY = 'hookhouse_studio_roulette_history';
const PENDING_KEY = 'hookhouse_studio_roulette_pending';
export const HISTORY_MAX = 50;
/** A Load tags the songs saved from the Studio tab for this long, until the next Load. */
export const PENDING_TTL_MS = 3 * 60 * 60 * 1000;

export interface RouletteHistoryEntry {
  roll: RouletteRoll;
  /** describeRoll text, so an entry is readable without decoding. */
  line: string;
  rolledAt: number;
  /** True once "Load into tabs" was pressed for this roll. */
  loaded: boolean;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the tab keeps working, it just will not remember */
  }
}

const isEntry = (e: unknown): e is RouletteHistoryEntry =>
  !!e && typeof e === 'object'
  && typeof (e as RouletteHistoryEntry).line === 'string'
  && typeof (e as RouletteHistoryEntry).roll?.seed === 'string'
  && Array.isArray((e as RouletteHistoryEntry).roll?.blends);

/** Newest first. Drops anything that does not look like an entry (hand-edited or from another version). */
export function loadHistory(): RouletteHistoryEntry[] {
  const list = read<unknown[]>(HISTORY_KEY);
  return Array.isArray(list) ? list.filter(isEntry).slice(0, HISTORY_MAX) : [];
}

export function addToHistory(roll: RouletteRoll, line: string, now = Date.now()): RouletteHistoryEntry[] {
  const next = [{ roll, line, rolledAt: now, loaded: false }, ...loadHistory()].slice(0, HISTORY_MAX);
  write(HISTORY_KEY, next);
  return next;
}

/** Marks the newest entry with this seed (and the same picks) as loaded. */
export function markLoaded(roll: RouletteRoll): RouletteHistoryEntry[] {
  const list = loadHistory();
  const i = list.findIndex((e) => e.roll.seed === roll.seed && JSON.stringify(e.roll) === JSON.stringify(roll));
  if (i >= 0) list[i] = { ...list[i], loaded: true };
  write(HISTORY_KEY, list);
  return list;
}

export function clearHistory(): void {
  write(HISTORY_KEY, []);
}

/** Remember which roll the Studio tab was just set up from. */
export function setPendingRoll(roll: RouletteRoll, now = Date.now()): void {
  write(PENDING_KEY, { seed: roll.seed, mode: roll.mode, loadedAt: now });
}

/**
 * Vault tags for a song saved from the Studio tab now: `studio-roulette,<mode>,seed:<seed>`, or null when
 * nothing was loaded, or the last Load is older than PENDING_TTL_MS (the settings have probably moved on).
 */
export function pendingRouletteTag(now = Date.now()): string | null {
  const p = read<{ seed?: string; mode?: string; loadedAt?: number }>(PENDING_KEY);
  if (!p || typeof p.seed !== 'string' || !/^\d{15}$/.test(p.seed) || typeof p.loadedAt !== 'number') return null;
  if (now - p.loadedAt > PENDING_TTL_MS) return null;
  return `studio-roulette,${p.mode === 'cursed' ? 'cursed' : 'random'},seed:${p.seed}`;
}
